/**
 * ⚡ Tốc độ trả lời — OpenAI Priority switch for the reply Shortcut, plus the
 * last 7 days of speed / cost per tier so anh can see whether it's worth it.
 * Worker: workers/kra-tools/src/reply-speed.ts (D1 tool_settings.reply_priority).
 */
import { useEffect, useState } from 'react';
import { getReplySpeed, setReplyPriority, type ReplySpeedResponse } from '../lib/api.js';

const YEN_PER_USD = 158;

const ENDPOINT_LABEL: Record<string, string> = {
  'reply-polish': 'Polish',
  'reply-polish-nail': 'Polish (nail)',
  'reply-auto': 'Auto',
  'reply-auto-nail': 'Auto (nail)',
  'reply-long': 'Tin dài',
};

const sec = (ms: number | null) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`);

export function ReplySpeedSection({
  token,
  onError,
}: {
  token: string | null;
  onError: (msg: string) => void;
}) {
  const [data, setData] = useState<ReplySpeedResponse | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getReplySpeed(token)
      .then(setData)
      .catch((err: Error) => onError(err.message));
  }, [token, onError]);

  async function toggle() {
    if (!data) return;
    const next = !data.priority_on;
    setSaving(true);
    try {
      await setReplyPriority(token, next);
      setData({ ...data, priority_on: next, updated_at: Date.now() });
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  if (!data) return null;

  return (
    <section className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">⚡ Tốc độ trả lời (Priority)</h2>
          <p className="text-xs text-muted mt-1">
            BẬT → OpenAI xử lý ưu tiên: nhanh + đều hơn, giá ~gấp đôi mỗi lần. Áp dụng cho Shortcut
            trả lời (polish / auto / tin dài), ăn sau ~30 giây.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={data.priority_on}
          disabled={saving}
          onClick={toggle}
          className={`shrink-0 relative w-12 h-7 rounded-full transition-colors disabled:opacity-50 ${
            data.priority_on ? 'bg-[#B76E6E]' : 'bg-card2 border border-border'
          }`}
        >
          <span
            className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
              data.priority_on ? 'left-6' : 'left-1'
            }`}
          />
        </button>
      </div>

      {data.by_tier.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted">
              <tr className="text-left">
                <th className="py-1 pr-3 font-normal">7 ngày</th>
                <th className="py-1 pr-3 font-normal">Kiểu</th>
                <th className="py-1 pr-3 font-normal text-right">Lần</th>
                <th className="py-1 pr-3 font-normal text-right">Tổng</th>
                <th className="py-1 pr-3 font-normal text-right">Chờ AI</th>
                <th className="py-1 font-normal text-right">¥/lần</th>
              </tr>
            </thead>
            <tbody>
              {data.by_tier.map((r) => (
                <tr key={`${r.endpoint}:${r.tier}`} className="border-t border-border">
                  <td className="py-1.5 pr-3">{ENDPOINT_LABEL[r.endpoint] ?? r.endpoint}</td>
                  <td className="py-1.5 pr-3">{r.tier === 'priority' ? '⚡ Priority' : 'Thường'}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{r.calls}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{sec(r.avg_ms)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{sec(r.avg_provider_ms)}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {(r.avg_cost_usd * YEN_PER_USD).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-muted mt-2">
            “Tổng” = từ lúc máy chủ nhận tới lúc trả lời; “Chờ AI” = phần đợi OpenAI, còn lại là
            máy chủ tự tìm câu mẫu/đọc cấu hình. Chưa tính thời gian iPhone chụp + đọc chữ + gửi ảnh.
          </p>
        </div>
      )}
    </section>
  );
}
